import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { Input as AntdInput, InputRef } from "antd";
import { TextAreaProps } from "antd/es/input";
import { TextAreaRef } from "antd/es/input/TextArea";
import { Size } from "@type/sizing";
import { HelperText } from "@components/helperText";
import { InputLabel } from "@components/inputLabel";
import { antdStatusMap, helperTextMap } from "@constants/input";
import { StyledInputContainer } from "./input.styles";
import { InputProps } from "./input.types";

const defaultClassNames = {
    prefix: "enigma-input-prefix",
    suffix: "enigma-input-suffix",
};

// Define the type for the compounded component
type CompoundedInputType = React.ForwardRefExoticComponent<
    // The compounded component's props are the union of the InputProps type and the InputRef type
    InputProps & React.RefAttributes<InputRef>
    // The compounded component also has a TextArea property that is of the same type as the DescriptionBox component
> & {
    TextArea: React.ForwardRefExoticComponent<
        TextAreaProps & {
            label?: string;
            helperText?: string;
            required?: boolean;
        } & React.RefAttributes<TextAreaRef>
    >;
};

const InternalInput = forwardRef<InputRef, InputProps>(
    (
        {
            label,
            helperText,
            status = "default",
            required,
            size = "medium",
            suffix,
            prefix,
            disabled,
            wrapperClassName,
            ...rest
        },
        ref
    ) => {
        const inputRef = useRef<InputRef>({} as InputRef);
        useImperativeHandle(ref, () => inputRef.current);

        return (
            <div className={wrapperClassName}>
                {label && (
                    <InputLabel
                        required={required}
                        onClick={() => {
                            inputRef.current.focus({ cursor: "end" });
                        }}
                    >
                        {label}
                    </InputLabel>
                )}

                <StyledInputContainer
                    size={Size[size]}
                    hasPrefix={Boolean(prefix)}
                    hasSuffix={Boolean(suffix)}
                >
                    <AntdInput
                        classNames={{
                            ...defaultClassNames,
                        }}
                        ref={inputRef}
                        status={antdStatusMap[status]}
                        size={Size[size]}
                        suffix={suffix}
                        prefix={prefix}
                        required={required}
                        disabled={disabled}
                        {...rest}
                    />
                </StyledInputContainer>

                {helperText && (
                    <HelperText
                        type={helperTextMap[status] || status}
                        disabled={disabled}
                    >
                        {helperText}
                    </HelperText>
                )}
            </div>
        );
    }
);

export const Input = InternalInput as CompoundedInputType;
InternalInput.displayName = "InternalInput";
Input.displayName = "Input";
// Add the antd TextArea component to the compounded input component
Input.TextArea = AntdInput.TextArea;
